/**
 * `ref.dangling` (§5.2 Reference integrity) — a reference points at an entity
 * that isn't in the live world: either an id that doesn't exist, OR one that
 * has been moved to Trash (soft-deleted). Both leave the reference broken — a
 * backlink to nothing, a relationship the reader can't follow.
 *
 * The referent may be a bare `ref` value OR the `parent` embedded in a
 * `containment` value (#206 — `contained_in` migrated from `ref` to
 * `containment`). `factRefTarget` resolves both shapes, keeping this rule
 * value-kind-agnostic.
 *
 * Severity: `error` at runtime (this rule's scope). The runtime case is
 * recoverable — the GM is mid-edit, or restores the trashed entity — so the
 * system accepts the write and surfaces a dismissible advisory with a one-click
 * "remove reference" fix. (Pack-import bumps this to `blocking_error`; that path
 * lands with the import auditor, not the entity-state pass here.)
 */
import { currentFactValue, factRefTarget } from '@dl/shared';

import type { RawAdvisory } from '../advisory.js';
import { factRef } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { forEachFact } from './helpers.js';

export function refDangling(ctx: AuditContext): RawAdvisory[] {
  const out: RawAdvisory[] = [];

  forEachFact(ctx.entities, ({ entity, factKey, predicate, fact }) => {
    const decl = ctx.registry.lookup(predicate);
    if (!decl) return;

    const value = currentFactValue(fact);
    // The referenced entity id, across the bare-ref and containment-parent
    // shapes. `null` ⇒ this predicate/value references nothing — skip (a
    // malformed value is a `value_kind_mismatch` rule's job).
    const target = factRefTarget(decl, value);
    if (target === null) return;
    const referent = ctx.entities[target];
    if (referent !== undefined && !referent.soft_deleted) return; // referent is live

    out.push({
      severity: 'error',
      rule: 'ref.dangling',
      message_key: 'auditor.ref.dangling',
      params: { predicate, target },
      fact_ids: [factRef(entity.id, factKey)],
      entity_ids: [entity.id],
      // One-click fix: drop the broken reference. Only offered for a bare
      // predicate slot (no instance/belief suffix) so the client's
      // predicate-keyed soft-delete applies cleanly.
      ...(factKey === predicate
        ? {
            fix: {
              kind: 'remove_fact' as const,
              subject: entity.id,
              factKey,
              label_key: 'auditor.fix.remove_reference',
              params: { predicate },
            },
          }
        : {}),
    });
  });

  return out;
}
